import { useMemo, useState } from 'react';
import { useI18n, useSignature } from '@react/hooks';
import { Button, Card, CardContent, ErrorAlert, Input } from '@react/components';
import { FormDialog } from '@/components/form/FormDialog';
import { StatusBadge } from '@/components/StatusBadge';
import { Select } from '@/components/Select';
import type {
  ContractSigningContext,
  Party,
  PartyProfile,
  SignatureProcedureResolution,
} from '@core/types/signature';

interface SigningProcedureDialogProps {
  party: Party;
  targetParties: Party[];
  onSaved: (context: ContractSigningContext) => void;
  onClose: () => void;
}

function initialProfile(party: Party): PartyProfile {
  return {
    entity: party.entity,
    version: 0,
    party_type: party.legal_entity_ref?.id ? 'company' : 'person',
    registration_country: '',
    signing_country: '',
    legal_form: '',
    tax_id: '',
    registration_id: '',
    display_name: party.display_name ?? '',
    legal_name: '',
    preferred_language: 'ru',
    representative_authority: 'direct',
    authority_documents: [],
    supported_signature_methods: ['internal_qualified'],
    supported_providers: [],
    preferred_providers: [],
    contact: { email: party.external_email ?? '', phone: party.external_phone ?? '' },
  };
}

export function SigningProcedureDialog({ party, targetParties, onSaved, onClose }: SigningProcedureDialogProps) {
  const { t } = useI18n();
  const store = useSignature();
  const [step, setStep] = useState(0);
  const [activePartyId, setActivePartyId] = useState(party.entity?.id ?? '');
  const [profiles, setProfiles] = useState<Record<string, PartyProfile>>(() => Object.fromEntries(
    targetParties.filter((item) => item.entity?.id).map((item) => [item.entity!.id, initialProfile(item)]),
  ));
  const [documentVersion, setDocumentVersion] = useState('1');
  const [contractType, setContractType] = useState('contract');
  const [governingLaw, setGoverningLaw] = useState('RU');
  const [language, setLanguage] = useState('ru');
  const [geography, setGeography] = useState('domestic');
  const [legalization, setLegalization] = useState('none');
  const [trustedTimestamp, setTrustedTimestamp] = useState('false');
  const [preparedContext, setPreparedContext] = useState<ContractSigningContext>();
  const [resolution, setResolution] = useState<SignatureProcedureResolution>();
  const [selectedProcedure, setSelectedProcedure] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const activeProfile = profiles[activePartyId];
  const candidates = useMemo(() => resolution?.candidates ?? [], [resolution]);
  const selected = candidates.find((candidate) => `${candidate.procedure?.code}:${candidate.procedure?.version}` === selectedProcedure);
  const questionnairesComplete = Object.values(profiles).every((profile) =>
    profile.registration_country.trim().length === 2
    && profile.signing_country.trim().length === 2
    && profile.display_name.trim()
    && profile.preferred_language.trim()
    && profile.representative_authority
    && (profile.party_type !== 'company' || profile.legal_name.trim()),
  );
  const contextComplete = documentVersion !== '' && Number(documentVersion) > 0 && contractType.trim() && governingLaw.trim() && language.trim();

  const updateProfile = <K extends keyof PartyProfile,>(field: K, value: PartyProfile[K]) => {
    setProfiles((current) => ({
      ...current,
      [activePartyId]: { ...current[activePartyId], [field]: value },
    }));
    setResolution(undefined);
    setSelectedProcedure('');
  };

  const buildContext = (procedureCode = '', procedureVersion = 0): ContractSigningContext => ({
    entity: preparedContext?.entity,
    document_id: party.target?.id ?? '',
    document_version: Number(documentVersion),
    contract_type: contractType.trim(),
    governing_law: governingLaw.trim().toUpperCase(),
    language: language.trim(),
    geography,
    signing_place: '',
    performance_place: '',
    parties: targetParties.filter((item) => item.entity?.id).map((item) => ({
      party_profile_id: item.entity!.id,
      role: item.role ?? '',
      profile: profiles[item.entity!.id],
    })),
    preferred_method: '',
    preferred_provider: '',
    regulated_industry: '',
    trusted_timestamp_required: trustedTimestamp === 'true',
    legalization,
    procedure_code: procedureCode,
    procedure_version: procedureVersion,
  });

  const prepare = async (procedureCode = '', procedureVersion = 0) => {
    setIsSaving(true);
    setError(null);
    try {
      const result = await store.PrepareSigning({ context: buildContext(procedureCode, procedureVersion) });
      setPreparedContext(result.context);
      setResolution(result.resolution);
      return result;
    } catch (err) {
      setError(err);
      return undefined;
    } finally {
      setIsSaving(false);
    }
  };

  const handleSubmit = async () => {
    if (step === 0) {
      setStep(1);
      return;
    }
    if (step === 1) {
      const result = await prepare();
      if (result?.resolution) {
        const firstReady = result.resolution.candidates?.find((candidate) => candidate.ready);
        setSelectedProcedure(firstReady ? `${firstReady.procedure?.code}:${firstReady.procedure?.version}` : '');
        setStep(2);
      }
      return;
    }
    if (!selected?.ready || !selected.procedure) return;
    const result = await prepare(selected.procedure.code, selected.procedure.version);
    if (result?.context) onSaved(result.context);
  };

  const steps = [t('signature.prepare.steps.questionnaires'), t('signature.prepare.steps.contract'), t('signature.prepare.steps.procedure')];

  return (
    <FormDialog
      open
      onClose={onClose}
      onSubmit={handleSubmit}
      submitLabel={step === 2 ? t('signature.prepare.saveProcedure') : t('common.next')}
      canSubmit={step === 0 ? Boolean(questionnairesComplete) : step === 1 ? Boolean(contextComplete) : Boolean(selected?.ready)}
      isSubmitting={isSaving}
      dirty
      width="lg"
      stableHeight
      aria-label={t('signature.prepare.title')}
      footerLeft={(
        <Button type="button" variant="secondary" onClick={() => setStep((current) => Math.max(0, current - 1))} disabled={isSaving || step === 0}>
          {t('common.back')}
        </Button>
      )}
    >
      <div className="flex min-h-full flex-col px-5 pb-5">
        <div className="border-b border-border pb-4">
          <h2 className="text-base font-semibold text-foreground">{t('signature.prepare.title')}</h2>
          <p className="mt-1 text-sm text-muted-foreground">{t('signature.prepare.subtitle')}</p>
          <ol className="mt-4 grid grid-cols-3 gap-2" aria-label={t('signature.prepare.steps.label')}>
            {steps.map((label, index) => (
              <li key={label} className={index === step ? 'text-sm font-medium text-foreground' : 'text-sm text-muted-foreground'} aria-current={index === step ? 'step' : undefined}>
                <span className="mr-1.5 font-mono text-xs">{index + 1}.</span>{label}
              </li>
            ))}
          </ol>
        </div>

        <div className="min-h-0 flex-1 py-4">
          {step === 0 && activeProfile && (
            <div className="space-y-4">
              <Select
                label={t('signature.prepare.party')}
                value={activePartyId}
                onChange={(event) => setActivePartyId(event.target.value)}
                options={targetParties.map((item) => ({ value: item.entity?.id ?? '', label: item.display_name || item.external_email || item.entity?.id || '' }))}
              />
              <div className="grid grid-cols-2 gap-3">
                <Select
                  label={t('signature.prepare.fields.partyType')}
                  value={activeProfile.party_type}
                  onChange={(event) => updateProfile('party_type', event.target.value)}
                  options={['person', 'company'].map((value) => ({ value, label: t(`signature.prepare.partyTypes.${value}`) }))}
                />
                <Select
                  label={t('signature.prepare.fields.authority')}
                  value={activeProfile.representative_authority}
                  onChange={(event) => updateProfile('representative_authority', event.target.value)}
                  options={['direct', 'power_of_attorney', 'corporate_role'].map((value) => ({ value, label: t(`signature.prepare.authority.${value}`) }))}
                />
                <Input label={t('signature.prepare.fields.registrationCountry')} value={activeProfile.registration_country} onChange={(event) => updateProfile('registration_country', event.target.value.toUpperCase())} maxLength={2} required />
                <Input label={t('signature.prepare.fields.signingCountry')} value={activeProfile.signing_country} onChange={(event) => updateProfile('signing_country', event.target.value.toUpperCase())} maxLength={2} required />
                <Input label={t('signature.prepare.fields.displayName')} value={activeProfile.display_name} onChange={(event) => updateProfile('display_name', event.target.value)} required />
                <Input label={t('signature.prepare.fields.legalName')} value={activeProfile.legal_name} onChange={(event) => updateProfile('legal_name', event.target.value)} required={activeProfile.party_type === 'company'} />
                <Input label={t('signature.prepare.fields.legalForm')} value={activeProfile.legal_form} onChange={(event) => updateProfile('legal_form', event.target.value)} />
                <Input label={t('signature.prepare.fields.registrationId')} value={activeProfile.registration_id} onChange={(event) => updateProfile('registration_id', event.target.value)} />
                <Input label={t('signature.prepare.fields.taxId')} value={activeProfile.tax_id} onChange={(event) => updateProfile('tax_id', event.target.value)} />
                <Input label={t('signature.prepare.fields.language')} value={activeProfile.preferred_language} onChange={(event) => updateProfile('preferred_language', event.target.value)} required />
              </div>
            </div>
          )}

          {step === 1 && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <Input label={t('signature.prepare.fields.contractType')} value={contractType} onChange={(event) => { setContractType(event.target.value); setResolution(undefined); }} required />
                <Input label={t('signature.prepare.fields.documentVersion')} type="number" min={1} value={documentVersion} onChange={(event) => setDocumentVersion(event.target.value)} required />
                <Input label={t('signature.prepare.fields.governingLaw')} value={governingLaw} onChange={(event) => setGoverningLaw(event.target.value.toUpperCase())} maxLength={2} required helperText={t('signature.prepare.fields.countryHelp')} />
                <Input label={t('signature.prepare.fields.contractLanguage')} value={language} onChange={(event) => setLanguage(event.target.value)} required />
                <Select label={t('signature.prepare.fields.geography')} value={geography} onChange={(event) => { setGeography(event.target.value); setResolution(undefined); }} options={['domestic', 'cross_border'].map((value) => ({ value, label: t(`signature.prepare.geography.${value}`) }))} />
                <Select label={t('signature.prepare.fields.legalization')} value={legalization} onChange={(event) => { setLegalization(event.target.value); setResolution(undefined); }} options={['none', 'notary', 'apostille', 'consular'].map((value) => ({ value, label: t(`signature.prepare.legalization.${value}`) }))} />
              </div>
              <p className="text-xs text-muted-foreground">{t('signature.prepare.legalizationHelp')}</p>
              <Select label={t('signature.prepare.fields.trustedTimestamp')} value={trustedTimestamp} onChange={(event) => { setTrustedTimestamp(event.target.value); setResolution(undefined); }} options={['false', 'true'].map((value) => ({ value, label: t(`common.${value === 'true' ? 'yes' : 'no'}`) }))} />
            </div>
          )}

          {step === 2 && (
            <div className="space-y-3">
              {candidates.length === 0 && <p className="text-sm text-muted-foreground">{t('signature.prepare.noProcedures')}</p>}
              {candidates.map((candidate) => {
                const procedure = candidate.procedure;
                if (!procedure) return null;
                const key = `${procedure.code}:${procedure.version}`;
                return (
                  <button key={key} type="button" className="block w-full text-left" onClick={() => setSelectedProcedure(key)} aria-pressed={selectedProcedure === key}>
                    <Card className={selectedProcedure === key ? 'border-primary ring-1 ring-primary' : 'hover:border-primary/40'}>
                      <CardContent className="space-y-2 py-3">
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <h3 className="text-sm font-medium text-foreground">{t(`signature.prepare.procedures.${procedure.code}`, { defaultValue: procedure.code })}</h3>
                            <p className="mt-1 text-xs text-muted-foreground">{t('signature.prepare.version', { version: procedure.version })}</p>
                          </div>
                          <StatusBadge variant={candidate.ready ? 'success' : 'warning'} label={t(candidate.ready ? 'signature.prepare.ready' : 'signature.prepare.incomplete')} size="sm" />
                        </div>
                        <p className="text-sm text-muted-foreground">{candidate.explanation}</p>
                        <p className="text-xs text-muted-foreground">{procedure.signature_method} · {procedure.provider_slug}{procedure.jurisdictions.length > 0 ? ` · ${procedure.jurisdictions.join(', ')}` : ''}</p>
                        {candidate.required_missing.map((issue) => <p key={`${issue.party_id}:${issue.code}:${issue.field}`} className="text-xs text-warning">{t(`signature.prepare.issues.${issue.code}`, { defaultValue: issue.message })}</p>)}
                      </CardContent>
                    </Card>
                  </button>
                );
              })}
              {resolution?.blockers.map((blocker) => <p key={`${blocker.procedure_code}:${blocker.code}`} className="text-sm text-warning">{t(`signature.prepare.issues.${blocker.code}`, { defaultValue: blocker.message })}</p>)}
            </div>
          )}
        </div>
        <div className="min-h-10">{error != null && <ErrorAlert error={error} dismissible onDismiss={() => setError(null)} />}</div>
      </div>
    </FormDialog>
  );
}
